import React from "react";
import { Button } from "@/components/ui/button";
import { HelpCircle, Lightbulb } from "lucide-react";
import { useOnboarding } from "./onboarding-provider";
import { OnboardingStep } from "./onboarding-tooltip";

interface OnboardingTriggerProps {
  steps: OnboardingStep[];
  buttonText?: string;
  variant?: "default" | "outline" | "ghost" | "link";
  size?: "default" | "sm" | "lg";
  icon?: boolean;
  className?: string;
}

export function OnboardingTrigger({
  steps,
  buttonText = "Spustiť sprievodcu",
  variant = "outline",
  size = "sm",
  icon = true,
  className
}: OnboardingTriggerProps) {
  const { startOnboarding, isActive } = useOnboarding();

  const handleStartOnboarding = () => {
    if (!isActive) {
      startOnboarding(steps);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleStartOnboarding}
      disabled={isActive}
      className={className}
    >
      {icon && <Lightbulb className="h-4 w-4 mr-2" />}
      {buttonText}
    </Button>
  );
}

// Help button specifically for dashboard
export function DashboardOnboardingTrigger() {
  const dashboardSteps: OnboardingStep[] = [
    {
      id: "welcome",
      target: "[data-onboarding='dashboard-header']",
      title: "Vitajte v TaskPro",
      description: "Toto je váš hlavný dashboard, kde môžete sledovať všetky dôležité informácie o vašich projektoch a úlohách.",
      position: "bottom",
      highlightPulse: true,
      action: { type: "none" }
    },
    {
      id: "stats",
      target: "[data-onboarding='task-statistics']",
      title: "Štatistiky úloh",
      description: "Tu vidíte prehľad všetkých vašich úloh rozdelených podľa stavu - dokončené, nevyriešené a označené úlohy.",
      position: "auto",
      highlightPulse: true,
      action: { type: "hover", label: "Prejdite myšou cez karty pre viac detailov" }
    },
    {
      id: "clients",
      target: "[data-onboarding='top-clients']",
      title: "Top klienti",
      description: "Prehľad najaktívnejších klientov podľa celkovej hodnoty úloh. Pomáha vám identifikovať najdôležitejších partnerov.",
      position: "auto",
      highlightPulse: true,
      action: { type: "none" }
    },
    {
      id: "starred-tasks",
      target: "[data-onboarding='starred-tasks']",
      title: "Označené úlohy",
      description: "Tu nájdete všetky priority úlohy, na ktoré sa momentálne zameriavate. Kliknite na úlohu pre viac detailov.",
      position: "top",
      highlightPulse: true,
      action: { type: "click", label: "Kliknite na akúkoľvek úlohu pre zobrazenie detailov" }
    },
    {
      id: "notifications",
      target: "[data-onboarding='notification-bell']",
      title: "Notifikácie",
      description: "Zvonček vám ukazuje počet urgentných úloh (dnes + po termíne). Kliknite pre zobrazenie všetkých notifikácií.",
      position: "bottom",
      highlightPulse: true,
      action: { type: "click", label: "Kliknite na zvonček" }
    },
    {
      id: "bottom-bar",
      target: "[data-onboarding='bottom-settings']",
      title: "Spodný panel",
      description: "V spodnej časti nájdete rýchly prístup k nastaveniam, profilu a urgent úlohám. Červené číslo ukazuje počet urgentných úloh.",
      position: "top",
      highlightPulse: true,
      action: { type: "none" }
    }
  ];

  return (
    <OnboardingTrigger
      steps={dashboardSteps}
      buttonText="Pomocník"
      variant="ghost"
      size="sm"
      className="text-muted-foreground hover:text-foreground"
    />
  );
}

// Generic help button for any page
export function PageHelpTrigger({ pageSteps, pageName }: { pageSteps: OnboardingStep[]; pageName: string }) {
  return (
    <OnboardingTrigger
      steps={pageSteps}
      buttonText={`Pomocník - ${pageName}`}
      variant="ghost"
      size="sm"
      className="text-muted-foreground hover:text-foreground"
      icon={true}
    />
  );
}